import { type ReactElement, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  generatePath, Navigate, useNavigate, useParams,
} from 'react-router-dom';
import { toast } from 'react-toastify';
import Button from 'components/Button/Button';
import EmptyList from 'components/EmptyList/EmptyList';
import Panel from 'components/Panel/Panel';
import { AppRoutes } from 'constants/AppRoutes';
import { BUTTON_VARIANT } from 'constants/interfaces';

import { formatIsoDateTime, payloadEntries } from './groupWatchRuns';
import IssueChatDialog from './IssueChatDialog';
import KnownListAddModal from './KnownListAddModal';
import { watchTowerRunHref } from './marketingOpsSelectors';
import {
  AFFECTED_STATUSES,
  companyNameById,
  findingByPublicId,
  logsForFinding,
  setAffectedLinkStatus,
  storySourceCard,
} from './marketingOpsStory';
import {
  TargetedMarketingGate,
  TargetedMarketingHeader,
} from './TargetedMarketingChrome';
import useWatchTowerMock from './useWatchTowerMock';
import { firstIdentifierLabel } from './watchTowerSelectors';

import type { WatchFindingAffected } from './types';

import styles from './scss/TargetedMarketing.module.scss';

function severityPillClass(severity: string): string {
  if (severity === 'high' || severity === 'critical') {
    return styles.statusFailed;
  }
  return styles.statusDraft;
}

function MarketingOpsFinding(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { publicId = '' } = useParams();
  const {
    state, update, liveActions,
  } = useWatchTowerMock();
  const finding = findingByPublicId(state, publicId);
  const [knownOpen, setKnownOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatId, setChatId] = useState(0);

  const card = useMemo(
    () => (finding ? storySourceCard(state, finding) : null),
    [finding, state],
  );
  const sourceCompany = useMemo(
    () => (finding
      ? state.watchCompanies.find((item) => item.id === finding.watchCompanyId) || null
      : null),
    [finding, state.watchCompanies],
  );
  const topic = useMemo(
    () => (finding?.watchTopicId
      ? (state.watchTopics || []).find((item) => item.id === finding.watchTopicId) || null
      : null),
    [finding, state.watchTopics],
  );
  const links = useMemo(
    () => (finding
      ? state.findingAffectedCompanies.filter((item) => item.findingId === finding.id)
      : []),
    [finding, state.findingAffectedCompanies],
  );
  const logs = useMemo(
    () => (finding ? logsForFinding(state, finding) : []),
    [finding, state],
  );
  const payload = finding ? payloadEntries(finding.payloadJson) : [];
  const sourcesJson = finding?.payloadJson?.sourcesJson;

  if (!finding || !card) {
    return <Navigate to={AppRoutes.marketingOps} replace />;
  }

  const onStatus = (linkId: number, status: string) => {
    update((prev) => setAffectedLinkStatus(prev, linkId, status));
    toast.success(t('targetedMarketing.statusSaved'));
  };

  const onPathA = () => {
    toast.error(t('targetedMarketing.discoveryNotLive'));
  };

  const onIssueChat = async () => {
    try {
      const chat = await liveActions.openIssueChat(finding);
      setChatId(chat.id);
      setChatOpen(true);
    } catch {
      toast.error(t('targetedMarketing.liveRequestFailed'));
    }
  };

  const dossierHref = (companyId: number) => generatePath(AppRoutes.watchTowerCompany, {
    id: String(companyId),
  });

  const sourceList = Array.isArray(sourcesJson)
    ? sourcesJson.map((item) => String(item))
    : [];

  return (
    <TargetedMarketingGate>
      <div className={styles.root}>
        <div className={styles.wrapper}>
          <TargetedMarketingHeader
            title={finding.title}
            backTo={AppRoutes.marketingOps}
            backLabel={t('targetedMarketing.marketingOpsTitle')}
            actions={(
              <div className={styles.toolbarActions}>
                {card.run ? (
                  <Button
                    title={t('targetedMarketing.openInWatchTower')}
                    variant={BUTTON_VARIANT.secondary}
                    onClick={() => {
                      const { run } = card;
                      if (run) {
                        navigate(watchTowerRunHref(run, finding.publicId));
                      }
                    }}
                  />
                ) : null}
                {finding.watchCompanyId ? (
                  <Button
                    title={t('targetedMarketing.openDossier')}
                    variant={BUTTON_VARIANT.secondary}
                    onClick={() => navigate(dossierHref(finding.watchCompanyId as number))}
                  />
                ) : null}
              </div>
            )}
          />

          <Panel className={styles.panel}>
            <span className={`${styles.statusPill} ${severityPillClass(finding.severity)}`}>
              {finding.severity}
            </span>
            <p className={styles.incidentMeta}>
              {card.companyName}
              {' · '}
              {card.country || t('targetedMarketing.none')}
              {' · '}
              {card.agentLabel}
              {' · '}
              {formatIsoDateTime(finding.observedAt)}
            </p>
            <p className={styles.muted}>{finding.publicId}</p>
          </Panel>

          {topic ? (
            <Panel className={styles.panel}>
              <h2 className={styles.sectionTitle}>{t('targetedMarketing.topicBlock')}</h2>
              <dl className={styles.payloadList}>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.title')}</dt>
                  <dd>{topic.title}</dd>
                </div>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.uid')}</dt>
                  <dd>{topic.uid}</dd>
                </div>
              </dl>
              <Button
                title={t('targetedMarketing.openTopic')}
                variant={BUTTON_VARIANT.secondary}
                onClick={() => navigate(generatePath(AppRoutes.watchTowerTopic, {
                  id: String(topic.id),
                }))}
              />
            </Panel>
          ) : null}

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.sourceSnapshot')}</h2>
            {sourceCompany ? (
              <dl className={styles.payloadList}>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.name')}</dt>
                  <dd>{sourceCompany.name}</dd>
                </div>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.uid')}</dt>
                  <dd>{sourceCompany.uid}</dd>
                </div>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.addressCountry')}</dt>
                  <dd>{sourceCompany.addressCountry}</dd>
                </div>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.industry')}</dt>
                  <dd>{sourceCompany.naceSection || t('targetedMarketing.none')}</dd>
                </div>
                <div className={styles.payloadRow}>
                  <dt>{t('targetedMarketing.identifiers')}</dt>
                  <dd>{firstIdentifierLabel(sourceCompany)}</dd>
                </div>
              </dl>
            ) : (
              <EmptyList text={t('targetedMarketing.noSourceCompany')} />
            )}
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.agentFound')}</h2>
            {finding.description ? <p>{finding.description}</p> : null}
            {payload.length === 0 ? (
              <p className={styles.muted}>{t('targetedMarketing.noPayload')}</p>
            ) : (
              <dl className={styles.payloadList}>
                {payload.map((entry) => (
                  <div key={entry.key} className={styles.payloadRow}>
                    <dt>{entry.key}</dt>
                    <dd>{entry.value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.sources')}</h2>
            {finding.sourceUrl ? (
              <p>
                <a href={finding.sourceUrl} target="_blank" rel="noreferrer">
                  {finding.sourceUrl}
                </a>
              </p>
            ) : (
              <p className={styles.muted}>{t('targetedMarketing.none')}</p>
            )}
            {sourceList.length > 0 ? (
              <ul>
                {sourceList.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.affectedCompanies')}</h2>
            {links.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyAffected')} />
            ) : (
              <div className={styles.tableWrap}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.affectedCompany')}</th>
                      <th>{t('targetedMarketing.relation')}</th>
                      <th>{t('targetedMarketing.status')}</th>
                      <th>{t('targetedMarketing.flaggedBy')}</th>
                      <th>{t('targetedMarketing.actions')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {links.map((link: WatchFindingAffected) => (
                      <tr key={link.id}>
                        <td>{companyNameById(state, link.affectedWatchCompanyId)}</td>
                        <td>{link.relationKind}</td>
                        <td>
                          <select
                            className={styles.filterNative}
                            aria-label={t('targetedMarketing.status')}
                            value={link.status}
                            onChange={(event) => onStatus(link.id, event.target.value)}
                          >
                            {AFFECTED_STATUSES.map((status) => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </select>
                        </td>
                        <td>{link.flaggedBy}</td>
                        <td>
                          <Button
                            title={t('targetedMarketing.openDossier')}
                            variant={BUTTON_VARIANT.secondary}
                            onClick={() => navigate(dossierHref(link.affectedWatchCompanyId))}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {links.map((link) => (
              link.talkingPoint ? (
                <p key={`talk-${link.id}`} className={styles.incidentImpact}>{link.talkingPoint}</p>
              ) : null
            ))}
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.identification')}</h2>
            <p className={styles.muted}>{t('targetedMarketing.pathHint')}</p>
            <div className={styles.actions}>
              <Button
                title={t('targetedMarketing.runDiscovery')}
                variant={BUTTON_VARIANT.secondary}
                onClick={onPathA}
              />
              <Button
                title={t('targetedMarketing.addKnownList')}
                variant={BUTTON_VARIANT.secondary}
                onClick={() => setKnownOpen(true)}
              />
              <Button
                title={t('targetedMarketing.openIssueChat')}
                variant={BUTTON_VARIANT.secondary}
                onClick={onIssueChat}
              />
            </div>
          </Panel>

          <Panel className={styles.panel}>
            <h2 className={styles.sectionTitle}>{t('targetedMarketing.digestLog')}</h2>
            {logs.length === 0 ? (
              <EmptyList text={t('targetedMarketing.emptyDigest')} />
            ) : (
              <div className={styles.tableWrap}>
                <table className={`${styles.table} ${styles.tableCompact}`}>
                  <thead>
                    <tr>
                      <th>{t('targetedMarketing.digestSent')}</th>
                      <th>{t('targetedMarketing.digestTo')}</th>
                      <th>{t('targetedMarketing.digestSubject')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {logs.map((row) => (
                      <tr key={row.id}>
                        <td>{formatIsoDateTime(row.sentAt)}</td>
                        <td>{row.to}</td>
                        <td>{row.subject}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
          {knownOpen ? (
            <KnownListAddModal
              open={knownOpen}
              finding={finding}
              state={state}
              onClose={() => setKnownOpen(false)}
              onApply={(next) => update(next)}
              onLiveAdd={finding.watchCompanyId
                ? async (buyerWatchCompanyId, watchProductId) => {
                  await liveActions.addKnownList({
                    publicId: finding.publicId,
                    buyerWatchCompanyId,
                    supplierWatchCompanyId: finding.watchCompanyId as number,
                    watchProductId,
                  });
                }
                : undefined}
            />
          ) : null}
          {chatOpen && chatId ? (
            <IssueChatDialog
              open={chatOpen}
              chatId={chatId}
              finding={finding}
              state={state}
              onClose={() => setChatOpen(false)}
              onApply={(next) => update(next)}
            />
          ) : null}
        </div>
      </div>
    </TargetedMarketingGate>
  );
}

export default MarketingOpsFinding;
